import * as THREE from 'three';
// Данные, а не модуль проекта: цикла зависимостей не создаёт (правило ниже
// про импорты в силе). Берём отсюда размер арены, чтобы `R` не разъезжался
// с `map.json` — раньше здесь стояло жёсткое 40, и любая правка карты
// молча ломала клампы клиента.
import mapData from '../../server/game/shared/map.json';

// ============================================================
//  Общее состояние и константы.
//
//  ВАЖНО про структуру: изменяемые скаляры лежат в объекте `S`, а не
//  отдельными `let`. ES-модули не дают импортёру присваивать импортированную
//  переменную (`firing = true` из input.js было бы ошибкой), поэтому всё,
//  что меняется из нескольких модулей, живёт полями объекта.
//
//  Этот модуль НИЧЕГО не импортирует из проекта — так разорваны все
//  циклические зависимости между модулями.
// ============================================================

export const isTouch = matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window;

// ---- Константы мира и поз ----
export const STAND = 1.7, CROUCH = 1.0, PRONE = 0.45;  // высота глаз стоя / присед / лёжа
export const BASE_FOV = 75;
// радиус арены, высота и толщина стен — из общего map.json, дублировать нельзя
export const R = mapData.arena.radius, WH = mapData.arena.wall_height, TH = mapData.arena.wall_thickness;
/** Насколько ниже верха ящика игрок ещё «стоит» на нём, а не толкается.
 *  ⚠️ Зеркало `shared/src/movement.rs::STEP_TOL` — менять только парой.
 *  0.45 м = 18 hammer units, шаг CS: карта собрана по габаритам CS. */
export const STEP_TOL = 0.45;
/** На сколько метров уезжают глаза при полном наклоне Q/E.
 *  ⚠️ Зеркало `shared/src/movement.rs::LEAN_OFFSET` — менять только парой. */
export const LEAN_OFFSET = 0.5;

export const clock = new THREE.Clock();

// ---- Игрок ----
export const player = {
  // ⚠️ Заглушка на один момент времени: `state.js` не импортирует из проекта
  // ничего (так разорваны циклы), а значит и карту тут спросить не у кого.
  // Настоящую точку ставит `world.js` сразу после сборки карты (`spawnPoint()`),
  // в игре — сервер. Числа здесь ничего не значат и опираться на них нельзя:
  // прежние `(0, STAND, 22)` пережили смену карты и дали баг-урок
  // «появляюсь под картой» (md/bug-lessons.md).
  pos: new THREE.Vector3(0, STAND, 0),
  vel: new THREE.Vector3(),
  onGround: true, radius: 0.35, hp: 100, alive: true,
  deadUntil: 0, lastHurt: 0, eye: STAND,
  // Выносливость 0..1 и ФАКТ бега в последнем шаге физики. Оба поля —
  // часть предсказываемого состояния (их считает player.js::stepPhysics и
  // присылает сервер в OP_SELF), поэтому живут рядом с позицией и скоростью,
  // а не в `S`: их переигрывает reconciliation.
  stamina: 1, sprinting: false,
  // Сколько секунд ещё держится сбитый попаданием шаг (зеркало
  // `movement.rs::Phys::hurt`). Пока больше нуля — бег запрещён, а скорость
  // умножена на HURT_SPEED_MUL. Тоже часть предсказываемого состояния:
  // сервер присылает остаток окна в OP_SELF (протокол 19).
  hurt: 0,
};

// ---- Изменяемое состояние ----
export const S = {
  yaw: Math.PI, pitch: 0,
  firing: false, semiQueued: false,
  // `ads` — НАМЕРЕНИЕ целиться (зажата ПКМ), `aiming` — целится ли на самом
  // деле. Разводить их пришлось из-за бега: на бегу оружие прижато к корпусу,
  // и зажатая ПКМ не делает ничего. Всё, что рисует прицеливание (FOV, поза
  // тела, перекрестье, чувствительность мыши, бит `BTN_ADS` в сеть), обязано
  // смотреть на `aiming` — иначе картинка разойдётся с тем, что считает
  // сервер (`movement::is_sprinting`). Обновляется в `weapons.js`.
  ads: false, aiming: false,
  // Бег: `sprinting` — бежит прямо сейчас (Shift + направление, зеркало
  // `movement.rs::is_sprinting`), `stow` — насколько оружие прижато к корпусу,
  // 0..1 (зеркало `movement.rs::step_stow`). ⚠️ `stow` — это И поза, И правило:
  // пока он больше нуля, выстрела нет ни здесь, ни на сервере.
  sprinting: false, stow: 0,
  proneToggle: false, crouchToggle: false,
  reloading: false, reloadEnd: 0,
  // `score` — свои убийства. ⚠️ С матчмейкинга это КОПИЯ серверной цифры
  // из кадра OP_SCORES (см. lobby.js), а не локальный счётчик: до этого он
  // тикал на каждом «убил» из OP_HIT и ни с чем не сверялся, то есть жил
  // отдельно от матча и обнулялся только перезагрузкой страницы.
  recoil: 0, score: 0,
  // Зажат Tab — показываем таблицу убийств (рисует lobby.js).
  showScores: false,
  shake: 0, bobT: 0, stepDist: 0,
  // Наклон Q/E. `lean` — намерение −1…1 (его и шлём серверу), `leanShift` —
  // фактическое смещение камеры в метрах после упора в стену.
  // ⚠️ Камера обязана ехать по leanShift, а не по lean: сервер считает
  // видимость и выстрелы именно из обрезанной точки.
  lean: 0, leanShift: 0,
  curKey: 'rifle',
  bloomOn: !isTouch,          // на телефоне блум по умолчанию выключен (тяжело)
  // Тени (клавиша N). На телефоне выключены сразу, на десктопе помнится выбор:
  // на слабой видеокарте это разница между 60 кадрами и 45 — см. md/bug-lessons.md.
  shadowsOn: !isTouch && (() => {
    try { return localStorage.getItem('fps_shadows') !== '0'; } catch { return true; }
  })(),
  started: false,
  // Отладочная строка F3: координаты и поверхность под ногами. Нужна не для
  // красоты: без неё игрок не может СКАЗАТЬ, где он стоит, и любая жалоба вида
  // «вот тут пол мерцает» превращается в поиск места по описанию.
  //
  // ⚠️ ВРЕМЕННО включена по умолчанию — на время разбора мерцающего пола
  // (2026-07-27). Причина: клавиша F3 до страницы не дошла вовсе (на клавиатуре
  // владельца F-ряд занят мультимедиа), а гадать «нажалось или нет» дороже, чем
  // показать строку всем. F3 остаётся тумблером, только теперь он ВЫКЛЮЧАЕТ.
  // ⚠️ Вернуть в `false`, когда разбор закончится.
  debugPos: true,
  // сеть
  nick: '',                   // ник игрока (сервер всё равно валидирует свой)
  ping: null,                 // сглаженный RTT в мс по ОСНОВНОМУ транспорту; null — связи нет
  pingAlt: null,              // то же по второму транспорту — он подключён только ради сравнения
  transport: 'ws',            // 'ws' | 'rtc' — по какому транспорту идёт игра (выбор на старте)
  // Мирность ботов 0..3 (см. net.js::DIFFICULTY). Выбирается на стартовом
  // экране и уезжает в кадре HELLO. ⚠️ Настройка КОМНАТНАЯ: комната одна
  // на всех, и вошедший последним меняет сложность всем — подробности
  // в server/src/npc.rs у `Difficulty`.
  difficulty: 0,
  netState: 'off',            // 'off' | 'connecting' | 'online' | 'error' (основного транспорта)
  interpDelay: 100,           // текущий буфер интерполяции, мс (адаптивный — см. remote.js)
  netJitter: 0,               // оценка СКО интервалов прихода снапшотов, мс
  // гранаты
  nadeMode: null,             // тип гранаты «в руке» (null — держим ствол)
  nadeCharge: 0,              // сила замаха 0..1
  lastNade: -10,
  // светошумовая
  flashAmt: 0, flashDecay: 0.3, flashHold: 0,
  // Молотов: горит ли САМ игрок. Онлайн — по биту F_BURNING в кадре OP_SELF
  // (правду знает сервер), офлайн — ставит сам `grenades.js`. По этому полю
  // рисуется оранжевая кайма экрана; урон к нему отношения не имеет.
  burning: false,
  burnVeil: 0,                // текущая непрозрачность каймы (гаснет плавно)
  // Таймеры догорания — ТОЛЬКО для офлайн-песочницы (зеркало Player::burn_*
  // на сервере). Онлайн их не трогает никто: там горение считает сервер.
  burnLeft: 0, burnNext: 0,
};

export const keys = {};
export const touchState = { moveX: 0, moveY: 0, crouch: false };

// ---- Общие коллекции (заполняются профильными модулями) ----
export const colliders = [];    // AABB стен/ящиков — world.js
export const wallMeshes = [];   // их меши для рейкастов — world.js
export const particles = [];    // искры/огонь — fx.js, grenades.js
export const decals = [];       // дырки от пуль — fx.js
export const splats = [];       // пятна крови на стенах — fx.js
export const tracers = [];      // трассеры — fx.js
export const grenades = [];     // летящие гранаты — grenades.js
export const smokes = [];       // облака дыма — grenades.js
export const fires = [];        // лужи огня (молотов) — grenades.js

/**
 * Направление взгляда из углов — копия shared/src/weapons.rs::aim_dir.
 *
 * Живёт здесь, а не в weapons.js, потому что нужна ещё и гранатам, а
 * grenades.js импортировать weapons.js не может (там уже есть обратный
 * импорт exitNade — вышел бы цикл). state.js ни от чего не зависит, поэтому
 * общая математика её законное место.
 *
 * ⚠️ Использовать S.yaw/S.pitch, а НЕ направление камеры: у камеры есть
 * отдача, покачивание, наклон и тряска, которых на сервере нет. Стрельба и
 * бросок обязаны считаться от того же угла, что уехал в протоколе.
 */
export function aimDir(yaw, pitch, out) {
  const sy = Math.sin(yaw), cy = Math.cos(yaw), sp = Math.sin(pitch), cp = Math.cos(pitch);
  return out.set(-sy * cp, sp, -cy * cp);
}

// ---- Переиспользуемые временные векторы (чтобы не мусорить в GC каждый кадр) ----
export const _wp = new THREE.Vector3();   // world position камеры
export const _wd = new THREE.Vector3();   // world direction камеры
